class TextRevealAnimation {
  constructor(selector) {
    this.elements = document.querySelectorAll(selector);
    this.description = document.querySelector('.anim-text__description');
    this.inset = 'inset(0 100% 0 0)';

    // Hide description initially if it exists
    if (this.description) {
      this.description.style.opacity = '0';
      this.description.style.transition = 'opacity 0.4s ease-in-out';
    }

    this.init();
  }

  // New method to parse HTML text and extract words with colors
  parseTextWithSpans(htmlText) {
    const words = [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlText.trim();
    // Function to traverse and extract text with colors
    const extractWords = (node, currentColor = null) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textWords = node.textContent.trim().split(/\s+/).filter(word => word.length > 0);
        textWords.forEach(word => {
          words.push({
            text: word,
            color: currentColor
          });
        });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        let nodeColor = currentColor;
        if (node.tagName.toLowerCase() === 'span' && node.style.color) {
          nodeColor = node.style.color;
        }
        Array.from(node.childNodes).forEach(child => {
          extractWords(child, nodeColor);
        });
      }
    };
    Array.from(tempDiv.childNodes).forEach(child => {
      extractWords(child);
    });
    
    return words;
  }

  init() {
    if (this.elements.length === 0) {
      return;
    }

    this.elements.forEach((element) => {
      const originalText = element.innerHTML;
      element.innerHTML = '';

      // Create main container
      const mainContainer = document.createElement('div');
      mainContainer.className = 'text-reveal-main-container';
      mainContainer.style.position = 'relative';

      // Parse text to get words with colors
      const wordsWithColors = this.parseTextWithSpans(originalText);

      wordsWithColors.forEach((wordObj, index) => {
        // Create container for each word
        const wordContainer = document.createElement('span');
        wordContainer.className = 'word-container';
        wordContainer.style.position = 'relative';
        wordContainer.style.display = 'inline-block';

        // Determine colors
        const fadeColor = wordObj.color ? this.adjustColorOpacity(wordObj.color, 0.2) : '#868686';
        const solidColor = wordObj.color || 'inherit'; // Use original color or inherit

        // Create faded text layer (background)
        const fadeWord = document.createElement('span');
        fadeWord.className = 'text-reveal-fade';
        fadeWord.innerHTML = wordObj.text;
        fadeWord.style.color = fadeColor;
        fadeWord.style.opacity = wordObj.color ? '0.3' : '0.2'; // Slightly more visible for colored text

        // Create solid text layer (foreground) with clip-path
        const revealWord = document.createElement('span');
        revealWord.className = 'text-reveal-solid';
        revealWord.innerHTML = wordObj.text;
        revealWord.style.position = 'absolute';
        revealWord.style.top = '0';
        revealWord.style.left = '0';
        revealWord.style.clipPath = this.inset;
        revealWord.style.webkitClipPath = this.inset;
        revealWord.style.color = solidColor;

        // Add elements to word container
        wordContainer.appendChild(fadeWord);
        wordContainer.appendChild(revealWord);

        // Save index to determine display order
        revealWord.dataset.index = index;

        // Add word container to main container
        mainContainer.appendChild(wordContainer);

        // Add space after each word (except last word)
        if (index < wordsWithColors.length - 1) {
          const space = document.createElement('span');
          space.innerHTML = '&nbsp;';
          mainContainer.appendChild(space);
        }
      });

      // Add main container to element
      element.appendChild(mainContainer);

      // Add element to scroll observation list
      this.observeElement(element, mainContainer);
    });

    // Save reference to bound scroll handler
    this.boundHandleScroll = this.handleScroll.bind(this);

    // Add event listener for scroll
    window.addEventListener('scroll', this.boundHandleScroll);
    this.isScrollListenerActive = true;
  }

  // Helper method to adjust color opacity for fade effect
  adjustColorOpacity(color, opacity) {
    // If it's a hex color
    if (color.startsWith('#')) {
      // Convert hex to rgb
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    
    // If it's rgb/rgba color
    if (color.startsWith('rgb')) {
      // Extract RGB values and create rgba
      const rgbMatch = color.match(/\d+/g);
      if (rgbMatch && rgbMatch.length >= 3) {
        return `rgba(${rgbMatch[0]}, ${rgbMatch[1]}, ${rgbMatch[2]}, ${opacity})`;
      }
    }
    
    // Fallback - return original color
    return color;
  }

  observeElement(element, container) {
    // Store information about element and words to display
    if (!this.observedElements) {
      this.observedElements = [];
    }

    const revealWords = Array.from(container.querySelectorAll('.text-reveal-solid'));

    this.observedElements.push({
      element,
      revealWords,
      elementTop: element.getBoundingClientRect().top + window.pageYOffset,
      elementHeight: element.offsetHeight,
      lastProgress: 0,
      totalWords: revealWords.length,
    });
  }

  calculateProgress(elementPositionInViewport, windowHeight) {
    const startPoint = windowHeight * 0.9;
    const endPoint = windowHeight / 2;
    const animationRange = startPoint - endPoint;

    if (elementPositionInViewport <= endPoint) {
      return 1;
    }
    if (elementPositionInViewport >= startPoint) {
      return 0;
    }
    return (startPoint - elementPositionInViewport) / animationRange;
  }

  handleScroll() {
    if (!this.observedElements) {
      return;
    }

    const windowHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    let allCompleted = true;

    this.observedElements.forEach((item) => {
      const elementPositionInViewport = item.elementTop - scrollTop;
      const progress = this.calculateProgress(elementPositionInViewport, windowHeight);

      if (progress < 1) {
        allCompleted = false;
      }

      if (progress !== item.lastProgress) {
        this.updateAnimation(item, progress);
        item.lastProgress = progress;
      }
    });

    if (allCompleted && this.isScrollListenerActive) {
      if (this.description && this.description.style.opacity === '0') {
        setTimeout(() => {
          this.description.style.opacity = '1';
        }, 100);
      }
      window.removeEventListener('scroll', this.boundHandleScroll);
      this.isScrollListenerActive = false;
    }
  }

  updateAnimation(item, progress) {
    // Calculate number of words to show based on progress
    const totalWordsToShow = Math.floor(progress * item.totalWords);

    // Update each word
    item.revealWords.forEach((word, index) => {
      if (index < totalWordsToShow) {
        // Word is complete
        word.style.clipPath = 'inset(0 0% 0 0)';
        word.style.webkitClipPath = 'inset(0 0% 0 0)';
      } else if (index === totalWordsToShow) {
        // Word is in progress
        const wordProgress = (progress * item.totalWords) - totalWordsToShow;
        const clipValue = `inset(0 ${100 - (wordProgress * 100)}% 0 0)`;
        word.style.clipPath = clipValue;
        word.style.webkitClipPath = clipValue;
      } else {
        // Word hasn't started revealing
        word.style.clipPath = this.inset;
        word.style.webkitClipPath = this.inset;
      }
    });
  }

  // Update element positions when window is resized
  updateElementPositions() {
    if (!this.observedElements) {
      return;
    }

    this.observedElements.forEach((item) => {
      item.elementTop = item.element.getBoundingClientRect().top + window.pageYOffset;
      item.elementHeight = item.element.offsetHeight;
    });

    // Trigger handleScroll to update display
    this.handleScroll();

    // If event listener was removed but needed again, add it back
    if (!this.isScrollListenerActive) {
      window.addEventListener('scroll', this.boundHandleScroll);
      this.isScrollListenerActive = true;
    }
  }
}

// Initialize animation when document is ready
jQuery(document).ready(($) => {
  const textReveal = new TextRevealAnimation('.anim-text__animation');

  // Update positions when window is resized
  $(window).on('resize', () => {
    textReveal.updateElementPositions();
  });

  // Trigger initial handleScroll to set initial state
  textReveal.handleScroll();
});